import type { RootState } from '@/app/store'
import Button from '@/components/Button'
import CampaignComponents from '@/components/CampaignComponents'
import Dialog from '@/components/Dialog/Dialog'
import DialogPopup from '@/components/Dialog/DialogPopup'
import Layout from '@/components/Layout'
import Loading from '@/components/Loading'
import NewsList from '@/components/NewsList'
import StampComponents from '@/components/StampComponents'
import { dialogGroupSpotsState } from '@/features/dialogGroupSpotsSlice'
import { dialogStatePoint } from '@/features/dialogSlice'
import { spotCheck } from '@/features/spotMapSlice'
import { urlUser, urlnotifications, urlusersheets } from '@/lib/custom'
import { urlsheets } from '@/lib/custom'
import { apiGet } from '@/lib/originalFetchApi'
import { useChangeLanguage } from '@/lib/useChangeLanguage'
import { usePaakAd } from '@/lib/usePaakAd'
import { useTracking } from '@/lib/useTracking'
import { time } from '@/lib/util'
import styles from '@/styles/Home.module.scss'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { useState, useRef, useEffect, Fragment, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
// @ts-ignore
import type { SwiperOptions } from 'swiper/types'
import useSWR from 'swr'

const Home = () => {
  const { t, i18n } = useTranslation()
  const swiperRef = useRef(null)
  const dispatch = useDispatch()
  const router = useRouter()
  useTracking()

  const [isAvailable, setAvailable] = useState<boolean>(true)
  const [isSwiperBeginning, setIsSwiperBeginning] = useState(true)
  const [isSwiperEnd, setIsSwiperEnd] = useState(false)

  const [dialogContentState, setDialogContentState] = useState<Api.UserSheetObject>()
  const [swiperIndex, setSwiperIndex] = useState(1)

  const dataCampaign = useSelector((state: RootState) => state.apiAsync.campaign)
  const dialogPointSelector = useSelector((state: RootState) => state.dialog.dialogStatePoint)
  const dialogGroupSpotsSelector = useSelector((state: RootState) => state.dialogGroupSpots.boolean)
  const groupSpotsSelector = useSelector((state: RootState) => state.dialogGroupSpots.spots)

  const { data: userState, error: userFetchError } = useSWR<Api.UserResBody>(`${urlUser}`, apiGet, {
    revalidateOnFocus: false,
  })

  const { data: FetchSheetsState, error: userSheetsFetchError } = useSWR<Api.UserSheetsResBody>(
    `${urlusersheets}`,
    apiGet,
    {
      revalidateOnFocus: false,
    },
  )

  const { data: notificationData, error: notificationFetchError } = useSWR<Api.NotificationsResBody>(
    `${urlnotifications}`,
    apiGet,
    {
      revalidateOnFocus: false,
    },
  )

  const { data: SpotsSheetsState, error: sheetsFetchError } = useSWR<Api.SheetsResBody>(`${urlsheets}`, apiGet, {
    revalidateOnFocus: false,
  })
  const goPrev = () => {
    swiperRef.current?.swiper?.slidePrev()
  }

  const goNext = () => {
    swiperRef.current?.swiper?.slideNext()
  }

  const params: SwiperOptions = useMemo(
    () => ({
      speed: 1000,
      centeredSlides: true,
      roundLengths: true,
      spaceBetween: 30,
      autoHeight: true,
      slidesPerView: 1,
      pagination: {
        el: '#pagination',
        type: 'bullets',
        clickable: true,
      },
      onSlideChange: (swiper) => {
        setSwiperIndex(swiper.realIndex + 1)
        setIsSwiperBeginning(swiper.isBeginning)
        setIsSwiperEnd(swiper.isEnd)
      },
      on: {
        slideChange: (swiper) => {
          setSwiperIndex(swiper.realIndex + 1)
          setIsSwiperBeginning(swiper.isBeginning)
          setIsSwiperEnd(swiper.isEnd)
        },
        onSwiper: (swiper) => {
          swiperRef.current = swiper
        },
      },
    }),
    [],
  )

  const popupAcquisitionStatus = useCallback(
    (content: Api.UserSheetObject) => {
      dispatch(dialogStatePoint(true))
      setDialogContentState(content)
    },
    [dispatch],
  )

  useEffect(() => {
    if (FetchSheetsState !== undefined && notificationData !== undefined) {
      setAvailable(false)
      return
    }
  }, [FetchSheetsState, notificationData])

  useChangeLanguage({ campaign: dataCampaign })

  const showCouponList = useCallback(() => {
    dispatch(spotCheck('coupon'))
    router.push({
      pathname: '/spot/',
    })
  }, [dispatch, router])

  // 広告効果計測
  usePaakAd({ campaign: dataCampaign, user: userState })

  if (isAvailable) return <Loading />
  if (userSheetsFetchError) return <div>{''}</div>
  if (notificationFetchError) return <div>{''}</div>
  if (sheetsFetchError) return <div>{''}</div>

  return (
    <Layout title={t('common.title')}>
      <CampaignComponents />
      {notificationData?.notifications && notificationData.notifications.length !== 0 ? (
        <section className="c-shadow_top bg-white mt-8 pt-6 pb-10">
          <div className="l-inner">
            <h2 className="text-center text-h2 font-bold">{t('home.notification', 'お知らせ')}</h2>
            <div className="my-8">
              {notificationData?.notifications[0] &&
                notificationData.notifications.slice(0, 3).map((content, i) => {
                  return (
                    <Fragment key={content.id}>
                      <NewsList
                        link="/news/detail/"
                        time={time({
                          time: content.startAt,
                          language: i18n.language,
                        })}
                        text={content.title}
                        query={`${content.id}`}
                      />
                    </Fragment>
                  )
                })}
            </div>
            <Button
              text={t('home.notification_list', 'お知らせ一覧へ')}
              width="w-9/12"
              bgColor="bg-[#fff]"
              onClick={() => {
                router.push('/news/')
              }}
            />
          </div>
        </section>
      ) : (
        ''
      )}
      <section className="my-8">
        <div className="l-inner">
          {/* ポイントラリーでコース数が1つの場合は総獲得ポイントを表示しない */}
          {dataCampaign.type === 3 || (dataCampaign.type === 2 && FetchSheetsState.sheets.length <= 2) ? (
            ''
          ) : (
            <>
              <h2 className="text-center text-h2 font-bold">
                {dataCampaign.type === 1 && t('home.collected_stamps', '獲得スタンプ')}
                {dataCampaign.type === 2 && t('home.total_points', '総獲得ポイント')}
              </h2>

              {dataCampaign.type === 2 && FetchSheetsState.sheets && (
                <div className="c-shadow mt-3 rounded-[4px] bg-[#fff] p-[10px] text-center">
                  <p className="text-[80px] font-bold">
                    {FetchSheetsState.sheets[0].earnedPoints}
                    <span className="text-[30px] font-bold">pt</span>
                  </p>
                </div>
              )}
            </>
          )}

          {/* type === 1 : スタンプ, type === 2 : ポイント, type === 3 : ポイント減算 */}
          {dataCampaign.type === 1 && (
            <div className="c-topSwiper relative mt-[18px]">
              <div id="pagination" className="flex justify-center space-x-1.75 mb-[10px]" />
              <p className="c-topSwiper_fraction">
                {swiperIndex} / {FetchSheetsState.sheets?.slice(1).length}
              </p>
              <button
                type="button"
                className="swiper-button-prev"
                onClick={goPrev}
                style={{
                  opacity: isSwiperBeginning ? '0' : '1',
                  pointerEvents: isSwiperBeginning ? 'none' : 'auto',
                }}
              >
                <img
                  src={`${process.env.NEXT_PUBLIC_SITEURL}/${process.env.NEXT_PUBLIC_SERVICE_NAME}/images/common/icon/icon_arrow_left.svg`}
                  alt=""
                />
              </button>
              <button
                type="button"
                className="swiper-button-next"
                onClick={goNext}
                style={{
                  opacity: isSwiperEnd || FetchSheetsState.sheets?.slice(1).length === 1 ? '0' : '1',
                  pointerEvents: isSwiperEnd || FetchSheetsState.sheets?.slice(1).length === 1 ? 'none' : 'auto',
                }}
              >
                <img
                  src={`${process.env.NEXT_PUBLIC_SITEURL}/${process.env.NEXT_PUBLIC_SERVICE_NAME}/images/common/icon/icon_arrow_right.svg`}
                  alt=""
                />
              </button>
              <Swiper modules={[Pagination]} {...params} ref={swiperRef}>
                {FetchSheetsState.sheets?.slice(1).map((content, i: number) => {
                  return (
                    <SwiperSlide key={content.id}>
                      <div key={content.id} className={`mt-[40px] ${styles.c_stamp_body}`}>
                        <div className="relative">
                          <h2 className="px-3 text-center text-sm font-bold">{content.name}</h2>
                        </div>
                        <div className="mt-5 mr-3 flex items-center justify-end">
                          <div className={styles.c_stamp_len}>
                            <p>{t('home.number_of_collected_stamps', 'スタンプ獲得数')}</p>
                            <p className="ml-4">
                              {dataCampaign.isRepeatStamp ? (
                                <>{content.hasCount}</>
                              ) : (
                                <>
                                  {content.hasCount} / {content.total}
                                </>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className={`flex flex-wrap ${styles.c_stamp_contents_wrap}`}>
                          {content.stamps?.map((contentStamp, i: number) => {
                            return (
                              <StampComponents
                                key={contentStamp.spotId}
                                query={contentStamp.spotId}
                                hasCount={contentStamp.hasCount}
                                course={content.name}
                                img={contentStamp.image}
                                name={contentStamp.name}
                                spots={contentStamp.spots}
                                dataCampaign={dataCampaign}
                              />
                            )
                          })}
                        </div>
                      </div>
                    </SwiperSlide>
                  )
                })}
              </Swiper>
            </div>
          )}

          {dataCampaign.type === 2 && (
            <div className="c-topSwiper relative mt-[25px]">
              <div id="pagination" className="flex justify-center space-x-1.75 mb-[10px]" />
              <p className="c-topSwiper_fraction">
                {swiperIndex} / {FetchSheetsState.sheets?.slice(1).length}
              </p>
              <button
                type="button"
                className="swiper-button-prev"
                onClick={goPrev}
                style={{
                  opacity: isSwiperBeginning ? '0' : '1',
                  pointerEvents: isSwiperBeginning ? 'none' : 'auto',
                }}
              >
                <img
                  src={`${process.env.NEXT_PUBLIC_SITEURL}/${process.env.NEXT_PUBLIC_SERVICE_NAME}/images/common/icon/icon_arrow_left.svg`}
                  alt=""
                />
              </button>
              <button
                type="button"
                className="swiper-button-next"
                onClick={goNext}
                style={{
                  opacity: isSwiperEnd || FetchSheetsState.sheets?.slice(1).length === 1 ? '0' : '1',
                  pointerEvents: isSwiperEnd || FetchSheetsState.sheets?.slice(1).length === 1 ? 'none' : 'auto',
                }}
              >
                <img
                  src={`${process.env.NEXT_PUBLIC_SITEURL}/${process.env.NEXT_PUBLIC_SERVICE_NAME}/images/common/icon/icon_arrow_right.svg`}
                  alt=""
                />
              </button>
              <Swiper modules={[Pagination]} {...params} ref={swiperRef}>
                {FetchSheetsState.sheets?.slice(1).map((content, i: number) => {
                  return (
                    <SwiperSlide key={content.id}>
                      <div key={content.id} className={`mt-[40px] ${styles.c_stamp_body}`}>
                        <div className="relative">
                          <h2 className="px-3 text-center text-sm font-bold">{content.name}</h2>
                        </div>
                        <div className="mx-3 mt-5 text-center">
                          <p className="text-[14px]">{t('home.total_point_in_course', 'コース累計獲得ポイント')}</p>
                          <p className="text-[48px] font-bold">
                            {content.earnedPoints}
                            <span className="text-[24px] font-bold">pt</span>
                          </p>
                        </div>
                        <p
                          className={styles.c_acquisitionStatus}
                          onClick={() => popupAcquisitionStatus(content)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              popupAcquisitionStatus(content)
                            }
                          }}
                        >
                          {t('home.collected_status_in_each_spot', 'スポットごとの獲得状況')}
                        </p>
                      </div>
                    </SwiperSlide>
                  )
                })}
              </Swiper>
            </div>
          )}

          {dataCampaign.type === 3 && (
            <div className="c-topSwiper relative mt-[25px]">
              <div id="pagination" className="flex justify-center space-x-1.75 mb-[10px]" />
              <p className="c-topSwiper_fraction">
                {swiperIndex} / {FetchSheetsState.sheets?.slice(1).length}
              </p>
              <button
                type="button"
                className="swiper-button-prev"
                onClick={goPrev}
                style={{
                  opacity: isSwiperBeginning ? '0' : '1',
                  pointerEvents: isSwiperBeginning ? 'none' : 'auto',
                }}
              >
                <img
                  src={`${process.env.NEXT_PUBLIC_SITEURL}/${process.env.NEXT_PUBLIC_SERVICE_NAME}/images/common/icon/icon_arrow_left.svg`}
                  alt=""
                />
              </button>
              <button
                type="button"
                className="swiper-button-next"
                onClick={goNext}
                style={{
                  opacity: isSwiperEnd || FetchSheetsState.sheets?.slice(1).length === 1 ? '0' : '1',
                  pointerEvents: isSwiperEnd || FetchSheetsState.sheets?.slice(1).length === 1 ? 'none' : 'auto',
                }}
              >
                <img
                  src={`${process.env.NEXT_PUBLIC_SITEURL}/${process.env.NEXT_PUBLIC_SERVICE_NAME}/images/common/icon/icon_arrow_right.svg`}
                  alt=""
                />
              </button>
              <Swiper modules={[Pagination]} {...params} ref={swiperRef}>
                {FetchSheetsState.sheets?.slice(1).map((content, i: number) => {
                  return (
                    <SwiperSlide key={content.id}>
                      <div key={content.id} className={`mt-[40px] ${styles.c_stamp_body}`}>
                        <div className="relative">
                          <h2 className="px-3 text-center text-sm font-bold">{content.name}</h2>
                        </div>
                        <div className="mx-3 mt-5 text-center">
                          <p className="text-[14px]">{t('home.remaining_point_in_course', 'コース別残ポイント')}</p>
                          <p className="text-[48px] font-bold">
                            {content.earnedPoints}
                            <span className="text-[24px] font-bold">pt</span>
                          </p>
                        </div>
                        <p
                          className={styles.c_acquisitionStatus}
                          onClick={() => router.push('/pointHistory')}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              router.push('/pointHistory')
                            }
                          }}
                        >
                          {t('home.point_history', 'ポイント履歴')}
                        </p>
                      </div>
                    </SwiperSlide>
                  )
                })}
              </Swiper>
            </div>
          )}
        </div>
      </section>
      {SpotsSheetsState?.sheets?.some((sheet) => sheet.id === 'coupon' && sheet.spots) && (
        <Button
          text={t('home.coupon_list', 'クーポン一覧へ')}
          width="w-9/12"
          borderColor="border-[#ff9bcd]"
          bgColor="bg-[#ff9bcd]"
          classes="mb-8"
          onClick={() => showCouponList()}
        />
      )}

      <Dialog
        title={t('home.collected_status_in_each_spot', 'スポットごとの獲得状況')}
        selectorState={dialogPointSelector}
        dispatchAction={dialogStatePoint}
      >
        <ul className="mt-[25px]">
          {dialogContentState?.stamps?.map((stamp, i: number) => {
            return (
              <li key={stamp.spotId} className="c_pointTableList">
                <table className="c_pointTable">
                  <tr>
                    <th>
                      <Link
                        href={{
                          pathname: '/spot/detail/',
                          query: {
                            query: stamp.spotId,
                          },
                        }}
                      >
                        <button
                          type="button"
                          className="text-[13px] font-semibold underline"
                          onClick={() => {
                            dispatch(dialogStatePoint(false))
                          }}
                        >
                          {stamp.name}
                        </button>
                      </Link>
                    </th>
                    <td className="text-[20px] font-bold">
                      {stamp.earnedPoints}
                      <span className="text-[16px]">pt</span>
                    </td>
                  </tr>
                  <tr>
                    <th className="text-[11px] font-light">
                      {t('home.point_where_you_get_stamp', 'このスポットで獲得できるポイント')}
                    </th>
                    <td className="text-[16px] font-light">
                      {stamp.point}
                      <span className="text-[12px]">pt</span>
                    </td>
                  </tr>
                </table>
              </li>
            )
          })}
        </ul>
      </Dialog>

      {/* スポットグループモーダル */}
      <Dialog
        title={t('home.spot_where_you_get_stamp', 'このスタンプを獲得できるスポット')}
        titleSmall
        selectorState={dialogGroupSpotsSelector}
        dispatchAction={dialogGroupSpotsState}
      >
        <ul className="mt-[25px]">
          {groupSpotsSelector?.map((spot, i: number) => {
            return (
              <li key={spot.id} className="c_pointTableList">
                <table className="c_pointTable">
                  <tr>
                    <th className="py-[7.25px]">
                      <Link
                        href={{
                          pathname: '/spot/detail/',
                          query: {
                            query: spot.id,
                          },
                        }}
                      >
                        <button
                          type="button"
                          className="text-[13px] font-semibold underline"
                          onClick={() => {
                            dispatch(dialogGroupSpotsState(false))
                          }}
                        >
                          {spot.name}
                        </button>
                      </Link>
                    </th>
                  </tr>
                  <tr>
                    <th className="py-[3.75px] text-[11px] font-light">{spot.address}</th>
                  </tr>
                </table>
              </li>
            )
          })}
        </ul>
      </Dialog>

      <DialogPopup hookpoint="HOME" />
    </Layout>
  )
}

export default Home
